

import React,  { useRef } from "react";
import headerImage from "../../assets/header13.jpg";
import { CopyBlock, dracula } from "react-code-blocks";
import w13_01 from "../../assets/w13_01.jpg"; // Baseplate in TinkerCAD
import w13_02 from "../../assets/w13_02.jpg"; // Stern auf Baseplate
import w13_03 from "../../assets/w13_03.jpg"; // Mehrere Sterne
import w13_04 from "../../assets/w13_04.jpg"; // Ausrichtung der Sterne
import w13_05 from "../../assets/w13_05.jpg"; // Komplette Stern-Base
import w13_07 from "../../assets/w13_07.jpg"; // STL fertig in TinkerCAD
import w13_08 from "../../assets/w13_08.jpg"; // MakerCase Box fertig
import w13_09 from "../../assets/w13_09.jpg"; // Box-Plan-Download-Ansicht
import w13_d from "../../assets/w13_.jpg"; // MakerCase Startseite



const Assignment14 = () => {
  const i2c = useRef(null);
  const display = useRef(null);
  const motordriver = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };


    
  return (

    <div className="container mx-auto my-8 px-5 max-w-3xl bg-gray-100 p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="relative mb-8">
        <img
          src={headerImage}
          alt="Header"
          className="w-full h-64 object-cover rounded-lg shadow-md  object-cover filter grayscale brightness-75"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
          <h1 className="text-4xl font-bold text-bg-secondary"  style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 1)' }}>Week 13: Molding and Casting</h1>
          <p className="text-gray-300 mt-2">Published on: <span className="font-semibold">June 03, 2025</span></p>
          <div className="mt-4 flex space-x-4">
         {/* Navigation Links */}


        </div>
        </div>
        </div>
        <div className="prose prose-lg max-w-3xl mx-auto my-8">
        <h2 className="text-2xl font-bold text-left mb-2">Custom Vegan Gummy Bear Mold</h2>

        <p className="text-gray-700 mb-4">
    My goal for the Assignment Molding and Casting is to create a reusable, food-safe silicone mold to make vegan fruit gummy bears. To achieve this, I will design a positive 3D model of the gummy bears, 3D print it, and embed it inside a laser-cut mold box to cast the silicone. The final silicone mold will then be used to cast gummy bears using an agar-agar recipe.
  </p>

  <h2 className="text-2xl font-bold text-left mb-2">Process Overview</h2>
<ol className="list-decimal pl-6 text-gray-700">
  <li>
    <strong>Designing the Mold Positive (TinkerCAD):</strong> The positive shape of the gummy candy is modeled using a CAD tool such as TinkerCAD. This model defines the final geometry of the castable shape.
  </li>
  <li>
    <strong>3D Printing the Positive:</strong> The CAD model is 3D printed using a suitable material like PETG. Since FDM printing often results in visible layer lines, post-processing such as sanding may be required to achieve a smooth surface finish.
  </li>
  <li>
    <strong>Creating the Mold Box:</strong> A mold enclosure is designed using a parametric box generator and cut from materials such as 3 mm acrylic. The box serves as a container for the silicone and should fit tightly around the printed part.
  </li>
  <li>
    <strong>Silicone Casting:</strong> The printed positive is placed inside the assembled mold box. A food-safe, two-part silicone is then poured around the positive to form the negative mold.
  </li>
  <li>
    <strong>Demolding:</strong> After full curing, the mold box is disassembled and the 3D print is carefully removed. The silicone mold is then cleaned and prepared for casting.
  </li>
  <li>
    <strong>Casting the Gummies:</strong> A vegan gummy mixture—typically made with agar-agar and fruit juice—is poured into the silicone mold to create the final edible pieces.
  </li>
</ol>

  <h2 className="text-2xl font-bold text-left mb-2 mt-2">Design the Mold Positive </h2>
  <p className="text-gray-700 mb-4">
  I designed the positive model for my silicone fruit gummy mold using <strong>TinkerCAD</strong>, an easy-to-use browser-based 3D modeling tool. My goal was to create a simple but fun shape – a star – and replicate it multiple times on a flat base to prepare it for molding. 
</p>

<p className="text-gray-700 mb-4">
  I started by dragging a flat <strong>box shape</strong> onto the workplane and resized it to create a baseplate with dimensions around <code>90 x 90 x 3 mm</code>. This would serve as the foundation for the entire positive mold and ensure that all star shapes sit on the same level.
</p>

<p className="text-gray-700 mb-4">
  Next, I added a <strong>star shape</strong> from the basic shape menu, set its dimensions to approximately <code>18 x 18 x 6 mm</code>, and placed it on the baseplate. I duplicated the star eight times and arranged them in a <strong>3 × 3 grid</strong> pattern. To ensure perfect alignment and spacing, I used TinkerCAD’s built-in <strong>“Align”</strong> and <strong>“Distribute”</strong> tools.
</p>

<p className="text-gray-700 mb-4">
  Once the stars were correctly positioned and centered on the baseplate, I selected all objects and used the <strong>“Group”</strong> function. This merged the stars and the base into a single solid object — making it ready for 3D printing and casting.
</p>

<p className="text-gray-700 mb-4">
  As the final step, I exported the grouped design as an <code>.STL</code> file, which I later used for FDM 3D printing. The exported file contains the full positive form that will be used to create the negative cavity in silicone.
</p>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
  <div>
    <img src={w13_d} alt="Baseplate in TinkerCAD" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">I created an 80×80×2 mm baseplate to serve as the foundation for my mold.</p>
  </div>
  <div>
    <img src={w13_01} alt="First star on baseplate" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">A single star shape was added and scaled to approximately 6 mm height — the planned gummy thickness.</p>
  </div>
  <div>
    <img src={w13_02} alt="Three stars in progress" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">I duplicated the star and placed it across the baseplate to begin forming a grid layout.</p>
  </div>
  <div>
    <img src={w13_03} alt="Aligning stars with the tool" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">TinkerCAD’s alignment tool helped evenly distribute and center the stars precisely.</p>
  </div>
  <div>
    <img src={w13_04} alt="Full star array" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">The final layout includes 9 stars, neatly arranged in a 3×3 grid, all grouped with the baseplate.</p>
  </div>
  <div>
    <img src={w13_05} alt="STL ready for export" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">Once finished, I exported the model as an <code>.STL</code> file for 3D printing.</p>
  </div>
</div>


  <h2 className="text-2xl font-bold text-left mb-2">3D Print and Post-Processing</h2>
  <p className="text-justify text-gray-700 mb-6">
       Since I haven't been to the Lab for a while, I unfortunately wasn't able to 3D print the mold.
        </p>
        <h2 className="text-2xl font-semibold mb-4">Creating the Laser-Cut Mold Box</h2>

<p className="text-gray-700 mb-4">
  To hold the 3D printed star mold in place while pouring silicone, I needed a custom-sized mold box. For this, I used a parametric <strong>box generator</strong> called <a href="https://www.makercase.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">MakerCase</a> to quickly generate a laser-cut-ready design.
</p>

<p className="text-gray-700 mb-4">
  I measured the dimensions of my printed mold base (approximately <code>80 × 80 mm</code>) and added a small margin on each side to give the silicone enough wall thickness. I entered the final internal dimensions into the box generator and set the material thickness to <strong>3 mm</strong>, since I used standard acryl for laser cutting.
</p>

<p className="text-gray-700 mb-4">
  The box generator created a downloadable <code>.SVG</code> file with finger joints that can be assembled easily.</p>
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
  <div>
    <img src={w13_07} alt="MakerCase start screen" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">I started with the MakerCase interface and selected a simple open-top box template.</p>
  </div>
  <div>
    <img src={w13_08} alt="Box preview in MakerCase" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">I set internal dimensions to 92×92×20 mm, allowing space for silicone walls and height clearance.</p>
  </div>
  <div>
    <img src={w13_09} alt="SVG layout of box panels" className="rounded shadow-md" />
    <p className="text-sm text-gray-600 mt-2">The software generated SVG plans with finger joints, ready to be laser cut from 3 mm acrylic.</p>
  </div>
</div>

    <p className="text-gray-700 mb-4">Next time I am in the Lab, I am going to laser cut the frame. </p>
    {/**
    
    I opened the SVG in our laser cutting software, adjusted the speed and power settings for 3 mm acryl, and cut the parts on the laser cutter.
</p>
 */}


<h2 className="text-2xl font-bold text-left mb-2">Silicone Casting</h2>
<p className="text-justify text-gray-700 mb-6">
  Once the positive mold has been 3D printed and the laser-cut mold box is assembled, I will be ready to pour the silicone into the mold. The silicone will flow around the star shapes and solidify into a flexible, food-safe casting mold.
</p>

<h2 className="text-2xl font-bold text-left mb-2">Gummy Casting</h2>
<p className="text-justify text-gray-700 mb-6">
  The next step will be to cast the vegan fruit gummies into the finished silicone mold using an agar-agar and juice mixture. I'm really looking forward to seeing the final results!
</p>

</div>



      <h2 className="text-2xl font-bold text-left mb-2">Downloads</h2>

      <a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/GummyStarMold.stl"
    download
  >
    📎 Download Star-STL
  </a>
  <a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/Box.svg"
    download
  >
    📎 Download Box 
  </a>

</div>

  );
};

export default Assignment14;
